import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { getStreetSearchBySettlement } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { Option } from "tm-shared/types";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { NewAddressFormValues } from "@/types/form";

import type { FieldName } from "./AddAddressModal.constants";

interface OptionWithAddress extends Option {
    address: string;
}

interface StreetFieldProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
    selectMode: boolean;
    setSelectMode: (value: boolean) => void;
}

export const StreetField: FC<StreetFieldProps> = ({
    activeMap,
    resetBelow,
    selectMode,
    setSelectMode,
}) => {
    const [searchQuery, setSearchQuery] = useState("");

    const { setValue, getValues, control } =
        useFormContext<NewAddressFormValues>();

    const target = useWatch<NewAddressFormValues, "target">({
        name: "target",
        control,
    });
    const house = useWatch<NewAddressFormValues, "house">({
        name: "house",
        control,
    });
    const settlement = useWatch<NewAddressFormValues, "settlement">({
        name: "settlement",
        control,
    });

    const handleChangeMode = useCallback(() => {
        setSelectMode(false);
    }, [setSelectMode]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("target", "");
            resetBelow("target");
        },
        [resetBelow, setValue],
    );

    const {
        options: fetchedOptions,
        loading,
        data,
    } = useAsyncOptions({
        fetchFn: (signal) => {
            const { settlement } = getValues();

            return getStreetSearchBySettlement(
                {
                    query: searchQuery,
                    settlement,
                },
                {
                    axiosOverrides: { signal },
                },
            );
        },
        selectData: (response) => response.result,
        mapToOption: (s) => ({ label: s.name, value: s.id }),
        dependencies: [searchQuery, settlement],
        enabled: (!searchQuery || searchQuery.length >= 3) && activeMap.target,
        debounceMs: 500,
    });

    const houseMap: Record<string, OptionWithAddress[]> = useMemo(() => {
        if (!data?.result) return {};

        return data?.result.reduce(
            (accumulator, street) => {
                accumulator[street.id] = street.addresses.map((a) => ({
                    label: a.hNum,
                    value: a.id,
                    address: a.name,
                }));

                return accumulator;
            },
            {} as Record<string, OptionWithAddress[]>,
        );
    }, [data?.result]);

    const houseOptionsForStreet = useMemo(
        () => houseMap[target] ?? [],
        [houseMap, target],
    );

    useEffect(() => {
        if (!house) return;

        const currentAddress = houseOptionsForStreet.find(
            (o) => o.value === house,
        );

        setValue("address", currentAddress?.address ?? null);
    }, [house, houseOptionsForStreet, setValue]);

    useEffect(() => {
        setSelectMode(true);
    }, [setSelectMode, target]);

    useEffect(() => {
        const currentStreetLabel = fetchedOptions.find(
            (o) => o.value === target,
        );

        setValue("streetLabel", currentStreetLabel?.label ?? "");
    }, [fetchedOptions, setValue, target]);

    return (
        <>
            <RHFAutocomplete
                name="target"
                options={fetchedOptions}
                label="Вулиця"
                rules={{
                    required: validationMessage.required,
                }}
                allowClear
                disabled={!activeMap.target}
                onAfterChange={() => resetBelow("target")}
                loading={loading}
                onInputChange={handleInputChange}
                selectProps={{
                    size: "large",
                }}
            />

            {selectMode && (
                <>
                    <RHFAutocomplete
                        name="house"
                        options={houseOptionsForStreet}
                        label="Будинок"
                        rules={{
                            required: validationMessage.required,
                        }}
                        allowClear={false}
                        disabled={!activeMap.house}
                        onAfterChange={() => resetBelow("house")}
                        actionOption={{
                            label: "Додати адресу",
                            onClick: handleChangeMode,
                        }}
                        selectProps={{
                            size: "large",
                        }}
                    />

                    <Controller
                        name={"address"}
                        control={control}
                        render={() => <></>}
                    />

                    <Controller
                        name={"streetLabel"}
                        control={control}
                        render={() => <></>}
                    />
                </>
            )}
        </>
    );
};
